import { useEffect, useState } from "react";

/**
 * CockpitHelpPanel
 * ----------------
 * Persistent "?" help button in the Cockpit corner that slides in a panel
 * explaining every tab, every action button, and where to find each tool.
 * Shannon's ask: "There's no directions or what, what does. That shouldn't
 * have never been taken out. I always need directions so I can master my
 * dashboard."
 *
 * Also exposes a "GENERATE VARIATION FROM PHOTO" launcher because the
 * PropertyPhotoRender modal was mounted in Cockpit but had no button to
 * open it — that's the bug where "there's no place I can drop pictures in
 * and they generate."
 *
 * Props:
 *   onOpenRender():  callback to open PropertyPhotoRender modal
 *   onOpenFieldMode(): callback to navigate to /field-mode
 *   onOpenFWAQR():     callback to navigate to /fwa-qr
 */

const CockpitHelpPanel = ({ onOpenRender, onOpenFieldMode, onOpenFWAQR }) => {
  const [open, setOpen] = useState(false);
  const [section, setSection] = useState("overview");

  // Auto-open on first visit ever
  useEffect(() => {
    try {
      const seen = localStorage.getItem("mf_help_seen_v1");
      if (!seen) {
        setOpen(true);
        localStorage.setItem("mf_help_seen_v1", new Date().toISOString());
      }
    } catch { /* ignore */ }
  }, []);

  const CYAN = "#00FFFF";
  const BG = "#020A1F";
  const WHITE = "#FFFFFF";
  const SILVER = "#A0A0A0";
  const MONO = "'JetBrains Mono', 'SF Mono', Menlo, monospace";

  return (
    <>
      {/* Floating ? button */}
      <button
        data-testid="cockpit-help-toggle"
        onClick={() => setOpen(true)}
        style={{
          /* Stacked above the FounderFire (✎) FAB so they never overlap
             on mobile — Feb 12, 2026 fix per Shannon's phone screenshots
             where all three bottom-right FABs were colliding. */
          position: "fixed", right: 18, bottom: "calc(env(safe-area-inset-bottom, 0px) + 96px)", zIndex: 900,
          width: 48, height: 48, borderRadius: "50%",
          background: `linear-gradient(135deg, ${CYAN} 0%, #00DC78 100%)`,
          color: BG, border: "none", cursor: "pointer",
          fontSize: "1.35rem", fontWeight: 900,
          boxShadow: `0 6px 20px rgba(0,255,255,0.35), 0 0 0 3px ${BG}`,
          display: open ? "none" : "flex", alignItems: "center", justifyContent: "center",
        }}
        aria-label="Open dashboard help">
        ?
      </button>

      {/* Slide-in help panel */}
      {open && (
        <div data-testid="cockpit-help-panel" role="dialog"
             onClick={() => setOpen(false)}
             style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 1000,
                      display: "flex", justifyContent: "flex-end" }}>
          <div onClick={(e) => e.stopPropagation()}
               style={{
                 width: "min(440px, 100%)", height: "100%", background: "#0A1930",
                 borderLeft: `1px solid ${CYAN}66`,
                 boxShadow: `-10px 0 40px rgba(0,255,255,0.15)`,
                 display: "flex", flexDirection: "column",
                 overflow: "hidden",
               }}>
            {/* Header */}
            <div style={{ padding: "1.1rem 1.2rem", borderBottom: `1px solid ${SILVER}22`,
                          display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ color: CYAN, fontFamily: MONO, fontSize: "0.66rem",
                              letterSpacing: "0.22em", fontWeight: 800 }}>
                  &gt; MOOVIN_FORWARD · HELP
                </div>
                <div style={{ color: WHITE, fontSize: "1.15rem", fontWeight: 700, marginTop: 3 }}>
                  How to run this dashboard
                </div>
              </div>
              <button data-testid="cockpit-help-close" onClick={() => setOpen(false)}
                      style={{ background: "transparent", color: SILVER, border: "none",
                               fontSize: "1.5rem", cursor: "pointer", padding: 4 }}>
                ×
              </button>
            </div>

            {/* Section chips */}
            <div style={{ padding: "0.7rem 1.2rem", display: "flex", gap: 6, flexWrap: "wrap",
                          borderBottom: `1px solid ${SILVER}22`, overflowX: "auto" }}>
              {[
                ["overview", "Overview"],
                ["tabs", "Tabs"],
                ["actions", "Action Buttons"],
                ["fwa", "FWA Expo"],
                ["urls", "URL Map"],
              ].map(([k, l]) => (
                <button key={k} data-testid={`help-chip-${k}`} onClick={() => setSection(k)}
                        style={{
                          background: section === k ? `${CYAN}25` : "transparent",
                          color: section === k ? CYAN : SILVER,
                          border: `1px solid ${section === k ? CYAN : SILVER + "33"}`,
                          borderRadius: 4, padding: "0.35rem 0.75rem",
                          fontFamily: MONO, fontSize: "0.66rem", fontWeight: 700,
                          letterSpacing: "0.1em", cursor: "pointer", whiteSpace: "nowrap",
                        }}>
                  {l.toUpperCase()}
                </button>
              ))}
            </div>

            {/* Body */}
            <div style={{ padding: "1.2rem", overflowY: "auto", flex: 1, color: WHITE, fontSize: "0.92rem", lineHeight: 1.65 }}>

              {section === "overview" && (
                <div data-testid="help-body-overview">
                  <h3 style={h3(CYAN)}>What this dashboard does</h3>
                  <p>This is Moovin Forward LLC&rsquo;s command center. Five things live here:</p>
                  <ol style={{ paddingLeft: "1.1rem", margin: "0.5rem 0 1rem" }}>
                    <li><strong style={{ color: CYAN }}>Signals</strong> — Market Sentinel finds acquisition/receivership opportunities in DFW</li>
                    <li><strong style={{ color: CYAN }}>Vendors</strong> — 1099 contractor network + application scoring</li>
                    <li><strong style={{ color: CYAN }}>Renders</strong> — Photo → Variation renders for owners and field use</li>
                    <li><strong style={{ color: CYAN }}>Money</strong> — Invoicing, bank tracking, doctrine (Service Fee = (Market Rent × 3) + 20%)</li>
                    <li><strong style={{ color: CYAN }}>Field</strong> — Live crew sessions, on-site tallies, photo capture</li>
                  </ol>

                  <div style={quickBtnRow}>
                    <button data-testid="help-open-render" onClick={() => { onOpenRender && onOpenRender(); setOpen(false); }}
                            style={primaryBtn(CYAN, BG)}>
                      📸 GENERATE VARIATION FROM PHOTO
                    </button>
                    <button data-testid="help-open-field" onClick={() => { onOpenFieldMode && onOpenFieldMode(); setOpen(false); }}
                            style={secondaryBtn(CYAN)}>
                      🛠️ OPEN FIELD MODE
                    </button>
                    <button data-testid="help-open-fwaqr" onClick={() => { onOpenFWAQR && onOpenFWAQR(); setOpen(false); }}
                            style={secondaryBtn(CYAN)}>
                      🎯 OPEN FWA BOOTH QR
                    </button>
                  </div>
                </div>
              )}

              {section === "tabs" && (
                <div data-testid="help-body-tabs">
                  <h3 style={h3(CYAN)}>What each tab does</h3>
                  <HelpItem title="Cockpit"     desc="Nervous system, digital platform COO, vibe board, community dispatch. Your morning check-in." />
                  <HelpItem title="Design Studio" desc="Upload a photo of any unit → pick one of the design variations → get a rendered before/after with SKU-anchored pricing. Public-safe for owners." />
                  <HelpItem title="Projects"    desc="Active properties. Add a new asset, unit configs, and lock scopes." />
                  <HelpItem title="Field Mode"  desc="On-site crew sessions. Photo capture, tally items, end-of-shift PDF report." />
                  <HelpItem title="Templates"   desc="Reusable proposals, invoices, and SOP kits." />
                  <HelpItem title="Vendor Network" desc="Approved 1099 contractors. Send warm invitations, track consent." />
                  <HelpItem title="Community Dispatch" desc="Community-first vendor tags — surfaces Black-led, Woman-led, Veteran, LGBTQ+ operators first when a scope opens." />
                </div>
              )}

              {section === "actions" && (
                <div data-testid="help-body-actions">
                  <h3 style={h3(CYAN)}>Cockpit action buttons</h3>
                  <HelpItem title="Send Proposal" desc="Sends a proposal PDF to an owner via Resend (email)." />
                  <HelpItem title="Invoice Owner" desc="Generates and emails an invoice ((Rent × 3) + 20%)." />
                  <HelpItem title="Doctrine"      desc="Opens the doctrine / rules-of-the-road reference for the firm." />
                  <HelpItem title="Refresh"       desc="Force-pulls the latest signals, sessions, vendor apps." />
                  <HelpItem title="Auto-Pilot"    desc="Toggles digital platform COO on/off for outreach + assignment routing." />
                  <HelpItem title="Bank Tracker"  desc="Live-tracks deposits, deposits-in-transit, service fee accrual." />
                  <HelpItem title="Vendor Hunter" desc="Manual search + one-click warm invite for local vendors." />
                  <HelpItem title="Send SMS"      desc="Bulk / individual Twilio SMS to the network. Sandbox in preview." />
                </div>
              )}

              {section === "fwa" && (
                <div data-testid="help-body-fwa">
                  <h3 style={h3(CYAN)}>FWA Expo · Aug 6, 2026</h3>
                  <HelpItem title="/fwa-qr" desc="Your booth screen. Display it on your phone — attendees point their camera at it to scan. No tapping needed." />
                  <HelpItem title="/fwa"    desc="Where the QR sends people. Identity gate captures name/company/seat/email, then routes them adaptively (owner → triage, vendor → apply, ADA → walkthrough, media/sponsor → capture)." />
                  <HelpItem title="/apply"  desc="Contractor & 1099 Worker application. Business or Individual toggle. Required Disclaimer. Fully protected under TUTSA + DTSA." />
                  <HelpItem title="Attendee Feed" desc="Recent FWA scans show up live inside Field Mode + Cockpit. Tap a row to text or email that person immediately." />
                  <div style={quickBtnRow}>
                    <button data-testid="help-fwa-open" onClick={() => { onOpenFWAQR && onOpenFWAQR(); setOpen(false); }}
                            style={primaryBtn(CYAN, BG)}>
                      🎯 OPEN /fwa-qr
                    </button>
                  </div>
                </div>
              )}

              {section === "urls" && (
                <div data-testid="help-body-urls">
                  <h3 style={h3(CYAN)}>The Four URLs You Actually Use</h3>
                  <UrlItem label="Public homepage" path="/" />
                  <UrlItem label="Sign in (yours)" path="/portal" />
                  <UrlItem label="FWA booth QR"    path="/fwa-qr" />
                  <UrlItem label="Contractor apply" path="/apply" />
                  <div style={{ height: 12 }} />
                  <h3 style={h3(CYAN)}>Bonus URLs</h3>
                  <UrlItem label="Field Mode"       path="/field-mode" />
                  <UrlItem label="Design Studio"    path="/design-studio" />
                  <UrlItem label="FWA event page"   path="/fwa" />
                  <UrlItem label="My personal QR"   path="/my-qr" />
                </div>
              )}
            </div>

            <div style={{ padding: "0.75rem 1.2rem", borderTop: `1px solid ${SILVER}22`,
                          color: SILVER, fontFamily: MONO, fontSize: "0.66rem", letterSpacing: "0.14em", textAlign: "center" }}>
              MOOVIN FORWARD LLC · v2.6 · FEB 2026
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const HelpItem = ({ title, desc }) => (
  <div style={{ padding: "0.6rem 0", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
    <div style={{ color: "#00FFFF", fontFamily: "'JetBrains Mono', monospace",
                  fontSize: "0.7rem", fontWeight: 800, letterSpacing: "0.14em",
                  textTransform: "uppercase", marginBottom: 3 }}>
      {title}
    </div>
    <div style={{ color: "#E6ECF2", fontSize: "0.86rem", lineHeight: 1.55 }}>{desc}</div>
  </div>
);

const UrlItem = ({ label, path }) => (
  <div style={{ padding: "0.5rem 0", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
    <div style={{ color: "#A0A0A0", fontSize: "0.75rem", marginBottom: 2 }}>{label}</div>
    <div style={{ color: "#FFFFFF", fontFamily: "'JetBrains Mono', monospace",
                  fontSize: "0.86rem", fontWeight: 700 }}>
      {path}
    </div>
  </div>
);

const h3 = (color) => ({
  color, fontFamily: "'JetBrains Mono', monospace",
  fontSize: "0.78rem", fontWeight: 800, letterSpacing: "0.14em",
  textTransform: "uppercase", margin: "0 0 0.7rem",
});

const quickBtnRow = {
  display: "flex", flexDirection: "column", gap: 8, marginTop: "1rem",
};

const primaryBtn = (cyan, bg) => ({
  background: cyan, color: bg, border: "none", padding: "0.85rem 1rem",
  borderRadius: 6, fontFamily: "'JetBrains Mono', monospace",
  fontSize: "0.78rem", fontWeight: 800, letterSpacing: "0.14em", cursor: "pointer",
});

const secondaryBtn = (cyan) => ({
  background: "transparent", color: cyan, border: `1px solid ${cyan}`,
  padding: "0.75rem 1rem", borderRadius: 6, fontFamily: "'JetBrains Mono', monospace",
  fontSize: "0.76rem", fontWeight: 700, letterSpacing: "0.14em", cursor: "pointer",
});

export default CockpitHelpPanel;
